<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import Modal from "@/components/Modal.vue";
import Steps from "@/components/parts/Steps.vue";
import NewHolidays from "@/components/NewHolidays.vue";
import StartEndTimes from "./StartEndTimes.vue";
import InputField from "@/components/forms/InputField.vue";
import { useMainStore } from "../stores/mainStore";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "close",
]);

const mainStore = useMainStore();
const router = useRouter();

const step = ref(1);
const dateRangeName = ref("");
const dates = ref({});
const dateRangeNameError = ref("");
const datesErrors = ref({
    startDate: "",
    endDate: "",
});

const holidays = ref({});

const showConfirmModal = ref(false);

function handleChange(data) {
    dates.value = data;
}

function handleHolidaysChange(data) {
    holidays.value = data;
}

async function handleSubmitModal() {
    if (step.value === 1) {
        dateRangeNameError.value = dateRangeName.value === "" || dateRangeName.value === null
            ? "Endter a course name"
            : "";

        datesErrors.value.startDate = dates.value?.start?.plain === null
            ? "Select start date"
            : "";

        datesErrors.value.endDate = dates.value?.end?.plain === null
            ? "Select end date"
            : "";

        if (Object.values(datesErrors.value)
            .filter((el) => el !== "").length === 0
            && dateRangeNameError.value === ""
        ) {
            step.value = 2;
        }

        return;
    }

    if (step.value === 2) {
        const termdateId = await mainStore.submitDuplicateModal(
            { name: dateRangeName.value, dates: dates.value, holidays: holidays.value },
        );

        if (termdateId !== null) {
            mainStore.showDuplicateModal = false;
            mainStore.duplicateTermId = null;
            router.push({ name: "Advanced term", params: { termdateId } });
        }
    }
}

function handleDeleteModal() {
    if (step.value === 1) {
        showConfirmModal.value = true;
    } else {
        step.value = 1;
    }
}

function handleCloseModal() {
    showConfirmModal.value = true;
}

function handleConfirmSubmit() {
    emit("close");
}

function handleConfirmClose() {
    showConfirmModal.value = false;
}

// eslint-disable-next-line no-unused-vars
const returnHeaderText = computed(() => {
    if (step.value === 1) {
        return "Duplicated schedule holiday";
    }
    return "Holiday (optional)";
});

</script>

<template>
    <Modal
        v-show="!showConfirmModal"
        :show-modal="props.show"
        :default-footer="false"
        close-btn-class="btn-new btn-new-outline ml-0"
        :close-text="step === 1 ? 'Cancel' : 'Back'"
        submit-text="Next"
        :simple-header="true"
        :show-spinner="mainStore.duplicateTermSubmitted"
        @submit-modal="handleSubmitModal"
        @close-modal="handleCloseModal"
        @submit-delete="handleDeleteModal"
    >
        <Steps
            :total-steps="2"
            :current-step="step"
            :show-step-items="false"
        />

        <div v-show="step === 1">
            <h2 class="mt-1">
                New date range
            </h2>

            <div class="info">
                Enter a new name and date range for your duplicated schedule
                (e.g. “Autumn Term 2021”). The date range is a container of
                time within which your events will recur.
            </div>

            <div class="d-flex flex-column schedule-name">
                <InputField
                    labelText="New course name"
                    placeholder="Enter a name"
                    :required="true"
                    v-model="dateRangeName"
                    :value="dateRangeName"
                    :error="dateRangeNameError"
                    :testId="'summary-venue-name-input'"
                    dataTest="input-venue-name"
                />
            </div>

            <div class="dates-block">
                <StartEndTimes
                    :errors="datesErrors"
                    @change="handleChange"
                />
            </div>
        </div>

        <div v-show="step === 2">
            <h2 class="mt-1">
                Holiday (optional)
            </h2>

            <div class="info mb-5">
                Are there any dates that <b>{{ dateRangeName || "" }}</b> is not
                running (e.g. half term or bank holidays)?
            </div>

            <NewHolidays
                :startDate="dates?.start?.plain"
                :endDate="dates?.end?.plain"
                start-holiday-label="Holiday start date (optional)"
                end-holiday-label="Holiday end date (optional)"
                @updated="handleHolidaysChange"
            />
        </div>
    </Modal>

    <Modal
        :show-modal="showConfirmModal"
        :remove-modal="true"
        title="Warning"
        close-text="No"
        submit-text="Yes"
        modal-dialog-class="modal-md"
        @submit-modal="handleConfirmSubmit"
        @close-modal="handleConfirmClose"
    >
        <div class="mt-5">
            Are you sure you want to close this window? Your progress will not be saved.
        </div>
    </Modal>
</template>

<style scoped>
h2 {
    font-style: normal;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2.25rem;
    color: #272529;
}

.info {
    font-style: normal;
    font-weight: 400;
    font-size: 0.85rem;
    line-height: 1.4rem;
    color: #636066;
}

.schedule-name {
    margin-top: 3rem;
}

.dates-block {
    margin-top: 1rem;
    margin-bottom: 3rem;
}
</style>
